import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { formatDisplayDate } from '../util/formatDate';
import { BRAND } from '../brand';
import DisposablePopup from '../components/DisposablePopup';
import AddDisposableModal from '../components/AddDisposableModal';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockAddHeaderButton from '../components/StockAddHeaderButton';
import useIsMobile from '../hooks/useIsMobile';

const Disposables = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [disposables, setDisposables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDisposable, setSelectedDisposable] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const itemsPerPage = 10;

  const fetchDisposables = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/disposables', { params });
      setDisposables(response.data.disposables || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch disposables');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchDisposables();
  }, [fetchDisposables]);

  const handleDisposableClick = (disposableId) => {
    if (isMobile) {
      navigate(`/disposables/${disposableId}`);
      return;
    }
    setSelectedDisposable(disposableId);
  };

  const handleClosePopup = () => {
    setSelectedDisposable(null);
    fetchDisposables();
  };

  const headerAction = useMemo(
    () => (
      <StockAddHeaderButton
        label="Add disposable"
        onClick={() => setAddModalOpen(true)}
      />
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const totalPages = Math.ceil(disposables.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDisposables = disposables.slice(startIndex, startIndex + itemsPerPage);

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, disposables.length)} of {disposables.length} disposables
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
    <StockPageLayout
      error={error && <Alert color="danger">{error}</Alert>}
      loading={loading}
      search={(
        <Card sx={{ mb: { xs: 1.5, md: 3 }, p: 2 }}>
          <FormControl sx={{ maxWidth: 400 }}>
            <FormLabel>Search by Disposable Name</FormLabel>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search disposables..."
              sx={{
                '&:hover': { borderColor: 'primary.500' },
                '&:focus-within': { borderColor: 'primary.500' }
              }}
            />
          </FormControl>
        </Card>
      )}
      pagination={paginationBar}
    >
          <StockMobileList empty={paginatedDisposables.length === 0} emptyMessage="No disposables found">
            {paginatedDisposables.map((disposable) => (
              <StockListCard
                key={disposable.id}
                title={disposable.disposable_name}
                onClick={() => handleDisposableClick(disposable.id)}
                fields={[
                  { label: 'Vendor', value: disposable.vendor_name || 'N/A' },
                  {
                    label: 'Rate (per item)',
                    value: disposable.rate != null
                      ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 })
                      : '—'
                  },
                  { label: 'Current Quantity', value: disposable.quantity },
                  { label: 'Created By', value: disposable.created_by || '—' },
                  {
                    label: 'Last Updated',
                    value: formatDisplayDate(disposable.last_updated, 'N/A')
                  }
                ]}
              />
            ))}
          </StockMobileList>

          <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: 'primary.500',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: 'primary.50',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Disposable Name</th>
                  <th style={{ width: '18%' }}>Vendor</th>
                  <th style={{ width: '12%' }}>Rate (per item)</th>
                  <th style={{ width: '12%' }}>Current Quantity</th>
                  <th style={{ width: '18%' }}>Created By</th>
                  <th style={{ width: '18%' }}>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDisposables.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      No disposables found
                    </td>
                  </tr>
                ) : (
                  paginatedDisposables.map((disposable) => (
                    <tr
                      key={disposable.id}
                      onClick={() => handleDisposableClick(disposable.id)}
                    >
                      <td style={{ color: BRAND.colors.navy, fontWeight: 'bold' }}>
                        {disposable.disposable_name}
                      </td>
                      <td>{disposable.vendor_name || 'N/A'}</td>
                      <td>{disposable.rate != null ? Number(disposable.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                      <td>{disposable.quantity}</td>
                      <td>{disposable.created_by || '—'}</td>
                      <td>
                        {formatDisplayDate(disposable.last_updated, 'N/A')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
    </StockPageLayout>

      {selectedDisposable && (
        <DisposablePopup
          disposableId={selectedDisposable}
          onClose={handleClosePopup}
        />
      )}

      <AddDisposableModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchDisposables}
      />
    </>
  );
};

export default Disposables;

